import * as React from "react";
import type { Column, Registrant } from "../api.js";
import { Cell } from "./Cell.js";
import { Icon } from "./Icon.js";

const AVATAR_PALETTE = [
  "#1a8fbf", "#14a89a", "#ff7a59", "#7c5cff", "#e94e77",
  "#f5a623", "#2ea66a", "#0f5f84", "#a8345a", "#0e7a70",
];

function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]!;
}

function initials(row: Registrant): string {
  if (row.firstName && row.lastName) return (row.firstName[0]! + row.lastName[0]!).toUpperCase();
  const parts = (row.fullName || "").split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return "?";
}

export function RegistrantDrawer({
  row,
  columns,
  onClose,
}: {
  row: Registrant;
  columns: Column[];
  onClose: () => void;
}) {
  const groupOrder = ["Identity", "Event", "Contact", "Membership", "Custom"] as const;
  const colsByGroup = new Map<string, Column[]>();
  for (const c of columns) {
    if (c.id === "fullName") continue;
    const list = colsByGroup.get(c.group) ?? [];
    list.push(c);
    colsByGroup.set(c.group, list);
  }

  const bg = avatarColor(row.contactId || row.fullName);

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer fade-in" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Registrant details">
        <div className="dr-head" style={{ background: `linear-gradient(135deg, ${bg}, ${bg}cc)` }}>
          <button className="dr-close" onClick={onClose} aria-label="Close drawer">
            <Icon name="x" size={16} />
          </button>
          <div className="dr-avatar">{initials(row)}</div>
          <div className="dr-name">{row.fullName}</div>
          {row.purchaserName && (
            <div className="dr-purchaser">Registered by {row.purchaserName}</div>
          )}
          <div className="dr-meta">{row.eventName}{row.ticketType ? ` · ${row.ticketType}` : ""}</div>
          <div className="dr-chips">
            <span className={`chip ${row.paid ? "green" : row.amountOwing > 0 ? "red" : "amber"}`}>
              {row.paid ? "Paid" : row.amountOwing > 0 ? `Owing $${row.amountOwing.toFixed(2)}` : "Unpaid"}
            </span>
            {row.membershipActive && (
              <span className="chip" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                {row.membershipLevel ?? "Member"}
              </span>
            )}
          </div>
        </div>
        <div className="dr-body">
          {groupOrder.map((group) => {
            const cols = colsByGroup.get(group) ?? [];
            if (!cols.length) return null;
            return (
              <div className="dr-section" key={group}>
                <h4>{group}</h4>
                {cols.map((c) => (
                  <div className="dr-row" key={c.id}>
                    <span className="lbl">{c.label}</span>
                    <span className="val"><Cell row={row} column={c} /></span>
                  </div>
                ))}
              </div>
            );
          })}
          {row.purchaserName && !row.attendeeContactKnown && (
            // Someone else bought this ticket and TidyHQ has no contact
            // record for the attendee, so the contact-scoped fields
            // above are blank rather than showing the buyer's values.
            // Say so explicitly: on an LCA event those fields carry
            // dietary and medical answers, and a blank that reads as
            // "nothing on file" would be a dangerous thing to guess at.
            <div className="dr-note">
              TidyHQ has no contact record we can read for
              {" "}{row.fullName}, so only the details that came with the
              registration are shown. Anything blank below is unknown,
              not empty &mdash; the values TidyHQ holds against this
              ticket belong to {row.purchaserName}, who bought it.
            </div>
          )}
          {row.purchaserName && (
            // Rendered as its own section (rather than back-filled into
            // the Contact fields) so a reader can always tell which
            // details belong to the attendee versus the person who
            // bought the ticket. Only appears when the two are
            // distinct.
            <div className="dr-section">
              <h4>Registered by</h4>
              <div className="dr-row"><span className="lbl">Name</span><span className="val">{row.purchaserName}</span></div>
              {row.purchaserEmail && (
                <div className="dr-row">
                  <span className="lbl">Email</span>
                  <span className="val"><a href={`mailto:${row.purchaserEmail}`}>{row.purchaserEmail}</a></span>
                </div>
              )}
              {row.purchaserPhone && (
                <div className="dr-row">
                  <span className="lbl">Phone</span>
                  <span className="val"><a href={`tel:${row.purchaserPhone}`}>{row.purchaserPhone}</a></span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="dr-foot">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {row.email && (
            <a className="btn btn-primary" href={`mailto:${row.email}`}>
              <Icon name="mail" size={14} /> Email
            </a>
          )}
        </div>
      </div>

      <style>{`
        .drawer-backdrop { position: fixed; inset: 0; background: rgba(13,27,42,0.4); z-index: 90; display: flex; justify-content: flex-end; }
        .drawer { width: 440px; max-width: 100vw; background: white; height: 100%; display: flex; flex-direction: column; box-shadow: -8px 0 24px rgba(13,27,42,0.15); }
        @keyframes drawerIn { from { transform: translateX(100%);} to { transform: none;} }
        .drawer { animation: drawerIn 280ms cubic-bezier(.2,.7,.2,1) both; }
        .dr-head { padding: 28px 24px 18px; color: white; position: relative; }
        .dr-close { position: absolute; top: 16px; right: 16px; width: 32px; height: 32px; border-radius: 50%; background: rgba(255,255,255,0.2); color: white; display: grid; place-content: center; border: 0; cursor: pointer; }
        .dr-close:hover { background: rgba(255,255,255,0.3); }
        .dr-avatar { width: 64px; height: 64px; border-radius: 50%; background: rgba(255,255,255,0.2); display: grid; place-content: center; font-size: 22px; font-weight: 800; margin-bottom: 12px; backdrop-filter: blur(4px); }
        .dr-name { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
        .dr-purchaser { font-size: 12.5px; opacity: 0.85; margin-top: 2px; }
        .dr-note { margin: 4px 0 16px; padding: 10px 12px; border-radius: 8px; background: var(--amber-50, #fff8e6); border: 1px solid var(--amber-200, #f2dca6); color: var(--ink-700, #4a4536); font-size: 12.5px; line-height: 1.45; }
        .dr-meta { font-size: 13px; opacity: 0.85; margin-top: 2px; }
        .dr-chips { display: flex; gap: 6px; margin-top: 12px; flex-wrap: wrap; }
        .dr-body { flex: 1; overflow: auto; padding: 16px 24px; }
        .dr-section { margin-bottom: 22px; }
        .dr-section h4 { font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-500); margin: 0 0 10px; }
        .dr-row { display: grid; grid-template-columns: 130px 1fr; gap: 12px; padding: 8px 0; border-bottom: 1px solid var(--ink-100); font-size: 13.5px; align-items: center; }
        .dr-row .lbl { color: var(--ink-500); display: flex; align-items: center; gap: 6px; }
        .dr-row .val { color: var(--ink-900); overflow: hidden; text-overflow: ellipsis; }
        .dr-foot { padding: 14px 20px; border-top: 1px solid var(--border); display: flex; gap: 8px; justify-content: flex-end; }
      `}</style>
    </div>
  );
}
